import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Server, 
  Shield, 
  Users, 
  Activity, 
  Wifi,
  Plus,
  Trash2,
  Edit,
  Power,
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  Cpu,
  HardDrive,
  Network,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const RADIUSManagement = () => {
  const [activeTab, setActiveTab] = useState('monitoring');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Hardware Monitoring data
  const [hardwareStats, setHardwareStats] = useState(null);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [radiusStats, setRadiusStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  
  // VPN Users
  const [vpnUsers, setVpnUsers] = useState([]);
  const [vpnConnections, setVpnConnections] = useState([]);
  const [showAddVpnUser, setShowAddVpnUser] = useState(false);
  const [newVpnUser, setNewVpnUser] = useState({ username: '', password: '', ip_address: '' });
  const [showPasswords, setShowPasswords] = useState({});
  
  // RADIUS Users
  const [radiusUsers, setRadiusUsers] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [nasClients, setNasClients] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (activeTab === 'monitoring') {
      fetchHardwareMonitoring();
    } else if (activeTab === 'dashboard') {
      fetchDashboard();
    } else if (activeTab === 'vpn-users') {
      fetchVpnUsers();
    } else if (activeTab === 'vpn-status') {
      fetchVpnConnections();
    } else if (activeTab === 'radius-users') {
      fetchRadiusUsers();
    } else if (activeTab === 'sessions') {
      fetchActiveSessions();
    } else if (activeTab === 'nas') {
      fetchNasClients();
    }
  }, [activeTab]);

  // NO AUTO-REFRESH - all data is refreshed on-demand by user
  // Passive architecture: no polling to external devices


  // Fetch Hardware Monitoring Data - From cache (fast)
  const fetchHardwareMonitoring = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Fetch data (will use cache if available)
      const [statsRes, servicesRes, radiusRes, alertsRes] = await Promise.all([
        axios.get(`${API_URL}/api/radius-hardware/stats`, { headers, timeout: 10000 }),
        axios.get(`${API_URL}/api/radius-hardware/services`, { headers, timeout: 10000 }),
        axios.get(`${API_URL}/api/radius-hardware/radius-stats`, { headers, timeout: 10000 }),
        axios.get(`${API_URL}/api/radius-hardware/alerts`, { headers, timeout: 10000 })
      ]);
      
      setHardwareStats(statsRes.data.data);
      setServiceStatus(servicesRes.data.data);
      setRadiusStats(radiusRes.data.data);
      setAlerts(alertsRes.data.data);
    } catch (error) {
      console.error('Error fetching hardware monitoring:', error);
      toast.error('Failed to fetch monitoring data');
    }
  };
  
  // Manual refresh - Clear cache and fetch fresh data
  const refreshMonitoring = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Call refresh endpoint (clears cache and fetches fresh)
      const response = await axios.post(`${API_URL}/api/radius-hardware/refresh`, {}, { headers, timeout: 15000 });
      
      if (response.data.success) {
        setHardwareStats(response.data.data.system);
        setServiceStatus(response.data.data.services);
        setRadiusStats(response.data.data.radius_stats);
        
        // Fetch alerts separately
        const alertsRes = await axios.get(`${API_URL}/api/radius-hardware/alerts`, { headers, timeout: 10000 });
        setAlerts(alertsRes.data.data);
        
        toast.success('Monitoring data refreshed');
      }
    } catch (error) {
      console.error('Error refreshing monitoring:', error);
      toast.error('Failed to refresh monitoring data');
    } finally {
      setTimeout(() => setRefreshing(false), 1000);
    }
  };

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/radius/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVpnUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/radius/vpn/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVpnUsers(response.data.data);
    } catch (error) {
      toast.error('Failed to load VPN users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVpnConnections = async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/radius/vpn/connections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVpnConnections(response.data.data);
    } catch (error) {
      console.error(error);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchRadiusUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/radius/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRadiusUsers(response.data.data);
    } catch (error) {
      toast.error('Failed to load RADIUS users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveSessions = async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/radius/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveSessions(response.data.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchNasClients = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/radius/nas-clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNasClients(response.data.data);
    } catch (error) {
      toast.error('Failed to load NAS clients');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVpnUser = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/v2/radius/vpn/users`, newVpnUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('VPN user created successfully');
      setShowAddVpnUser(false);
      setNewVpnUser({ username: '', password: '', ip_address: '' });
      fetchVpnUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create VPN user');
    }
  };

  const handleDeleteVpnUser = async (username) => {
    if (!window.confirm(`Delete VPN user ${username}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/v2/radius/vpn/users/${username}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('VPN user deleted');
      fetchVpnUsers();
    } catch (error) {
      toast.error('Failed to delete VPN user');
    }
  };

  const handleDisconnectVpn = async (username) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/v2/radius/vpn/disconnect/${username}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Disconnected ${username}`);
      fetchVpnConnections();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewVpnUser({ ...newVpnUser, password });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const togglePasswordVisibility = (username) => {
    setShowPasswords({ ...showPasswords, [username]: !showPasswords[username] });
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-indigo-100 rounded-lg dark:bg-indigo-900/40">
            <Server className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">RADIUS & VPN Management</h1>
            <p className="text-sm text-gray-600 dark:text-slate-300">Monitor and manage RADIUS server & VPN connections</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-slate-700">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {[
            { id: 'monitoring', label: 'Server Monitoring', icon: Cpu },
            { id: 'dashboard', label: 'Dashboard', icon: Activity },
            { id: 'vpn-users', label: 'VPN Users', icon: Shield },
            { id: 'vpn-status', label: 'VPN Status', icon: Wifi },
            { id: 'radius-users', label: 'RADIUS Users', icon: Users },
            { id: 'sessions', label: 'Active Sessions', icon: Activity },
            { id: 'nas', label: 'NAS Clients', icon: Server }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Server Monitoring Tab */}
      {activeTab === 'monitoring' && (
        <div className="space-y-6">
          {/* Alerts Section */}
          {alerts && alerts.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-yellow-900/30">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                <h3 className="font-semibold text-yellow-900">Active Alerts ({alerts.length})</h3>
              </div>
              <div className="space-y-2">
                {alerts.map((alert, idx) => (
                  <div key={idx} className={`flex items-start gap-2 text-sm p-2 rounded ${
                    alert.severity === 'critical' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    <AlertTriangle className="h-4 w-4 mt-0.5" />
                    <span>{alert.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* System Resources */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>System Resources</CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={refreshMonitoring}
                  disabled={refreshing}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {hardwareStats ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* CPU Usage */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Cpu className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <span className="font-medium">CPU Usage</span>
                      </div>
                      <span className={`text-xl font-bold ${hardwareStats.cpu_usage > 80 ? 'text-red-600' : 'text-gray-900'}`}>
                        {hardwareStats.cpu_usage?.toFixed(1) || '0.0'}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-slate-700">
                      <div 
                        className={`h-2 rounded-full ${hardwareStats.cpu_usage > 80 ? 'bg-red-600' : 'bg-blue-600'}`}
                        style={{ width: `${Math.min(hardwareStats.cpu_usage, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* RAM Usage */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <HardDrive className="h-5 w-5 text-green-600 dark:text-green-400" />
                        <span className="font-medium">RAM Usage</span>
                      </div>
                      <span className={`text-xl font-bold ${hardwareStats.ram_usage_percent > 90 ? 'text-red-600' : 'text-gray-900'}`}>
                        {hardwareStats.ram_usage_percent?.toFixed(1) || '0.0'}%
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 mb-2 dark:text-slate-300">
                      {hardwareStats.ram_used_gb || 0} GB / {hardwareStats.ram_total_gb || 0} GB
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-slate-700">
                      <div 
                        className={`h-2 rounded-full ${hardwareStats.ram_usage_percent > 90 ? 'bg-red-600' : 'bg-green-600'}`}
                        style={{ width: `${Math.min(hardwareStats.ram_usage_percent, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Disk Usage */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <HardDrive className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        <span className="font-medium">Disk Space</span>
                      </div>
                      <span className={`text-xl font-bold ${hardwareStats.disk_usage_percent > 85 ? 'text-red-600' : 'text-gray-900'}`}>
                        {hardwareStats.disk_usage_percent?.toFixed(1) || '0.0'}%
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 mb-2 dark:text-slate-300">
                      {hardwareStats.disk_used_gb || 0} GB / {hardwareStats.disk_total_gb || 0} GB
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-slate-700">
                      <div 
                        className={`h-2 rounded-full ${hardwareStats.disk_usage_percent > 85 ? 'bg-red-600' : 'bg-purple-600'}`}
                        style={{ width: `${Math.min(hardwareStats.disk_usage_percent, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Uptime */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      <span className="font-medium">Server Uptime</span>
                    </div>
                    <div className="text-xl font-bold text-gray-900 dark:text-slate-100">{hardwareStats.uptime || 'N/A'}</div>
                  </div>

                  {/* Load Average */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                      <span className="font-medium">Load Average</span>
                    </div>
                    <div className="text-sm text-gray-700 dark:text-slate-200">
                      1m: <span className="font-bold">{hardwareStats.load_average?.[0]?.toFixed(2) || '0.00'}</span>
                      {' '} | 5m: <span className="font-bold">{hardwareStats.load_average?.[1]?.toFixed(2) || '0.00'}</span>
                      {' '} | 15m: <span className="font-bold">{hardwareStats.load_average?.[2]?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>

                  {/* Network */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Network className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                      <span className="font-medium">Network Traffic</span>
                    </div>
                    <div className="text-sm text-gray-700 dark:text-slate-200">
                      <div>RX: {hardwareStats.network_rx_mbps?.toFixed(2) || '0.00'} MB</div>
                      <div>TX: {hardwareStats.network_tx_mbps?.toFixed(2) || '0.00'} MB</div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                      Total since boot
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-slate-400">Loading system resources...</div>
              )}
            </CardContent>
          </Card>

          {/* Service Status */}
          <Card>
            <CardHeader>
              <CardTitle>Service Status</CardTitle>
            </CardHeader>
            <CardContent>
              {serviceStatus ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {Object.entries(serviceStatus)
                    .filter(([key, service]) => typeof service === 'object' && service.name)
                    .map(([key, service]) => (
                    <div key={key} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">{service.name}</span>
                        {service.active ? (
                          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                        )}
                      </div>
                      <Badge variant={service.active ? "success" : "destructive"} className={service.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {service.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-slate-400">Loading service status...</div>
              )}
            </CardContent>
          </Card>

          {/* RADIUS Statistics */}
          <Card>
            <CardHeader>
              <CardTitle>RADIUS Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              {radiusStats ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="border rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1 dark:text-slate-300">Total Auth Requests</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-slate-100">{radiusStats.total_auth_requests.toLocaleString()}</div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1 dark:text-slate-300">Success Rate</div>
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">{radiusStats.success_rate?.toFixed(1) || '0.0'}%</div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1 dark:text-slate-300">Failed Auths</div>
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">{radiusStats.failed_auths.toLocaleString()}</div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1 dark:text-slate-300">Active Sessions</div>
                    <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{radiusStats.active_sessions}</div>
                  </div>
                  <div className="border rounded-lg p-4 md:col-span-2">
                    <div className="flex items-center gap-2">
                      <div className="text-sm text-gray-600 dark:text-slate-300">Database Connection:</div>
                      <Badge variant={radiusStats.db_connected ? "success" : "destructive"} className={radiusStats.db_connected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {radiusStats.db_connected ? 'Connected' : 'Disconnected'}
                      </Badge>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-slate-400">Loading RADIUS statistics...</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}


      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-3 text-center py-8">Loading...</div>
          ) : dashboardData ? (
            <>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600 dark:text-slate-300">VPN Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                    {dashboardData.vpn.total_users}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Total configured users</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600 dark:text-slate-300">VPN Active</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                    {dashboardData.vpn.active_connections}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Connected now</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600 dark:text-slate-300">RADIUS Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                    {dashboardData.radius.total_users}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Total users</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600 dark:text-slate-300">Active Sessions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                    {dashboardData.radius.active_sessions}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">PPPoE/Hotspot active</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600 dark:text-slate-300">NAS Clients</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                    {dashboardData.radius.nas_clients}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Mikrotik routers</p>
                </CardContent>
              </Card>
            </>
          ) : null}
        </div>
      )}

      {/* VPN Users Tab */}
      {activeTab === 'vpn-users' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>VPN Users Management</CardTitle>
              <Button onClick={() => setShowAddVpnUser(true)} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add VPN User
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {showAddVpnUser && (
              <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50 dark:border-slate-700 dark:bg-slate-900">
                <h3 className="font-semibold mb-4">Add New VPN User</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Username *</Label>
                    <Input
                      value={newVpnUser.username}
                      onChange={(e) => setNewVpnUser({ ...newVpnUser, username: e.target.value })}
                      placeholder="mikrotik1"
                    />
                  </div>
                  <div>
                    <Label>Password *</Label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={newVpnUser.password}
                        onChange={(e) => setNewVpnUser({ ...newVpnUser, password: e.target.value })}
                        placeholder="Strong password"
                      />
                      <Button onClick={generatePassword} size="sm" variant="outline">
                        Generate
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label>IP Address (optional)</Label>
                    <Input
                      value={newVpnUser.ip_address}
                      onChange={(e) => setNewVpnUser({ ...newVpnUser, ip_address: e.target.value })}
                      placeholder="Auto assign"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleAddVpnUser}>Create User</Button>
                  <Button onClick={() => setShowAddVpnUser(false)} variant="outline">Cancel</Button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b dark:bg-slate-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Username</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Password</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">IP Address</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {vpnUsers.map((user) => (
                    <tr key={user.username} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                      <td className="px-4 py-3 text-sm font-medium">{user.username}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded dark:bg-slate-800">
                            {showPasswords[user.username] ? user.password : '••••••••'}
                          </code>
                          <button
                            onClick={() => togglePasswordVisibility(user.username)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            {showPasswords[user.username] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={() => copyToClipboard(user.password)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <code className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded dark:bg-blue-900/30 dark:text-blue-300">
                          {user.ip_address}
                        </code>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Badge variant="secondary">{user.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <Button
                          onClick={() => handleDeleteVpnUser(user.username)}
                          size="sm"
                          variant="destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {vpnUsers.length === 0 && !loading && (
                <div className="text-center py-8 text-gray-500 dark:text-slate-400">No VPN users configured</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* VPN Status Tab */}
      {activeTab === 'vpn-status' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Active VPN Connections</CardTitle>
              <Button onClick={fetchVpnConnections} size="sm" variant="outline" disabled={refreshing}>
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b dark:bg-slate-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Username</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Interface</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Client IP</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Server IP</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {vpnConnections.map((conn, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                      <td className="px-4 py-3 text-sm font-medium">{conn.username}</td>
                      <td className="px-4 py-3 text-sm">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded dark:bg-slate-800">{conn.interface}</code>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <code className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded dark:bg-blue-900/30 dark:text-blue-300">{conn.client_ip}</code>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <code className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded dark:bg-green-900/30 dark:text-green-300">{conn.server_ip}</code>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Badge className="bg-green-500">{conn.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <Button
                          onClick={() => handleDisconnectVpn(conn.username)}
                          size="sm"
                          variant="destructive"
                        >
                          <Power className="h-4 w-4 mr-1" />
                          Disconnect
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {vpnConnections.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-slate-400">No active VPN connections</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* RADIUS Users Tab */}
      {activeTab === 'radius-users' && (
        <Card>
          <CardHeader>
            <CardTitle>RADIUS Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b dark:bg-slate-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Username</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Password Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {radiusUsers.map((user, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                      <td className="px-4 py-3 text-sm font-medium">{user.username}</td>
                      <td className="px-4 py-3 text-sm">
                        <Badge variant="secondary">{user.check_attribute}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {radiusUsers.length === 0 && !loading && (
                <div className="text-center py-8 text-gray-500 dark:text-slate-400">No RADIUS users found</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Sessions Tab */}
      {activeTab === 'sessions' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Active PPPoE/Hotspot Sessions</CardTitle>
              <Button onClick={fetchActiveSessions} size="sm" variant="outline" disabled={refreshing}>
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b dark:bg-slate-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Username</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">NAS IP</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Upload</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Download</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {activeSessions.map((session, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                      <td className="px-4 py-3 text-sm font-medium">{session.username}</td>
                      <td className="px-4 py-3 text-sm">
                        <code className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded dark:bg-blue-900/30 dark:text-blue-300">
                          {session.nasipaddress}
                        </code>
                      </td>
                      <td className="px-4 py-3 text-sm">{formatDuration(session.acctsessiontime)}</td>
                      <td className="px-4 py-3 text-sm">{formatBytes(session.acctinputoctets)}</td>
                      <td className="px-4 py-3 text-sm">{formatBytes(session.acctoutputoctets)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {activeSessions.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-slate-400">No active sessions</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* NAS Clients Tab */}
      {activeTab === 'nas' && (
        <Card>
          <CardHeader>
            <CardTitle>NAS Clients (Mikrotik Routers)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b dark:bg-slate-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">IP Address</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400">Secret</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {nasClients.map((nas, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                      <td className="px-4 py-3 text-sm font-medium">{nas.shortname}</td>
                      <td className="px-4 py-3 text-sm">
                        <code className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded dark:bg-blue-900/30 dark:text-blue-300">
                          {nas.nasname}
                        </code>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Badge variant="secondary">{nas.type}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded dark:bg-slate-800">{'•'.repeat(8)}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {nasClients.length === 0 && !loading && (
                <div className="text-center py-8 text-gray-500 dark:text-slate-400">No NAS clients configured</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RADIUSManagement;